"use client";

/**
 * The chats attached to a project, most-recently-(created) first.
 *
 * Reuses the proven `workspace-tasks` query (which loads each task's
 * `thread` + `currently_attached_to`) and filters client-side to the
 * tasks attached to `projectId`. A dedicated server-side query is awkward
 * because `Project.current_tasks` (the reverse of `CurrentTaskAttachedTo`)
 * is an injected instance-only accessor — see `rflp_tasks/api.py`.
 *
 * `workspace-tasks` already orders by `-started_at`, so the result is the
 * project's chats newest-first: index 0 is the default chat that opens on
 * project entry (ProjectLayout auto-open), and the list backs the switcher.
 */

import React from "react";
import { Workspace } from "@sourcepilot/generated/base/api/workspace";
import { graphStore, graphStoreJotaiStore } from "@sourcepilot/framework/graph-store";
import { QueryNames } from '@sourcepilot/generated/query-names';

const CHAT_TASK_TYPES = ["ChatTask", "ChatSkillsTask", "RFLPTask"];

export interface ProjectChat {
  /** The attached Task node (carries `id`, `_label`, loaded `thread`). */
  task: any;
}

export interface ProjectChats {
  chats: ProjectChat[];
}

/** Read a task's `currently_attached_to` target id straight from the
 *  GraphStore (the `workspace-tasks` load populated it). */
function attachedProjectId(taskId: string): string | null {
  try {
    const atom = graphStore.getRelationshipListAtom(taskId, "currently_attached_to");
    const relIds = (graphStoreJotaiStore.get(atom) as string[]) ?? [];
    for (const rid of relIds) {
      const target = graphStore.getRelationship(rid)?.targetId;
      if (target) return target;
    }
  } catch {
    /* store not ready / no edge */
  }
  return null;
}

export function useProjectChats(projectId: string | null): ProjectChats {
  const [tasks, setTasks] = React.useState<any[]>([]);

  React.useEffect(() => {
    if (!projectId) {
      setTasks([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await Workspace.setQuery(QueryNames.tasksWorkspaceTasks)
          .tasks.filter({ __type: CHAT_TASK_TYPES })
          .load({ thread: {}, currently_attached_to: {} })
          .all();
        if (!cancelled) setTasks((res as any[]) ?? []);
      } catch (e) {
        console.error("[useProjectChats] failed to load chats", e);
        if (!cancelled) setTasks([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return React.useMemo(
    () => ({
      chats: tasks
        .filter((t) => attachedProjectId(String(t.id)) === projectId)
        .map((t) => ({ task: t })),
    }),
    [tasks, projectId],
  );
}
